import { useEffect, useMemo, useRef, useState } from "react";

import {
  getProgressTitle,
  PROGRESS_STATUSES,
} from "./../../config/progressConfigs";

import ProgressItem from "./ProgressItem";
import "./progress.css";

function getOverallStatus(items, isProcessing, error) {
  if (
    error ||
    items.some(
      (item) => item.status === PROGRESS_STATUSES.FAILED
    )
  ) {
    return PROGRESS_STATUSES.FAILED;
  }

  if (
    isProcessing ||
    items.some(
      (item) => item.status === PROGRESS_STATUSES.STARTED
    )
  ) {
    return PROGRESS_STATUSES.STARTED;
  }

  if (
    items.length > 0 &&
    items.every(
      (item) => item.status === PROGRESS_STATUSES.COMPLETED
    )
  ) {
    return PROGRESS_STATUSES.COMPLETED;
  }

  return null;
}

function getSummaryItem(items) {
  // Prefer the latest item that is still in progress.
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (items[index].status === PROGRESS_STATUSES.STARTED) {
      return items[index];
    }
  }

  return items[items.length - 1] || null;
}

function getSummaryText(items, isProcessing, error) {
  const summaryItem = getSummaryItem(items);

  if (summaryItem) {
    return getProgressTitle(summaryItem);
  }

  if (error) {
    return "پردازش درخواست ناموفق بود";
  }

  if (isProcessing) {
    return "در انتظار دریافت وضعیت پردازش...";
  }

  return "وضعیت پردازش";
}

function OverallStatusIcon({ status }) {
  if (status === PROGRESS_STATUSES.COMPLETED) {
    return (
      <span
        className="progress-summary__status-icon progress-summary__status-icon--completed"
        aria-hidden="true"
      >
        ✓
      </span>
    );
  }

  if (status === PROGRESS_STATUSES.FAILED) {
    return (
      <span
        className="progress-summary__status-icon progress-summary__status-icon--failed"
        aria-hidden="true"
      >
        !
      </span>
    );
  }

  return (
    <span
      className="progress-summary__status-icon progress-summary__status-icon--started"
      aria-hidden="true"
    />
  );
}

export default function ProgressPanel({
  items,
  error,
  isProcessing,
  isExpanded,
  setIsExpanded
}) {
  const contentRef = useRef(null);

  const overallStatus = useMemo(
    () => getOverallStatus(items, isProcessing, error),
    [items, isProcessing, error]
  );

  const summaryText = useMemo(
    () => getSummaryText(items, isProcessing, error),
    [items, isProcessing, error]
  );

  /*
   * The panel is controlled only by the user.
   * New progress events must NOT open or close it automatically.
   *
   * When it is already open, scroll to the latest item whenever the
   * list changes. The same effect also scrolls to the bottom immediately
   * after the user opens the panel.
   */
  useEffect(() => {
    if (!isExpanded || !contentRef.current) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      const content = contentRef.current;

      if (!content) {
        return;
      }

      content.scrollTo({
        top: content.scrollHeight,
        behavior: "smooth",
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isExpanded, items]);

  if (!isProcessing && items.length === 0 && !error) {
    return null;
  }

  function toggleExpanded() {
    setIsExpanded((currentValue) => !currentValue);
  }

  return (
    <section
      className={`
        progress-panel
        progress-panel--${overallStatus || "idle"}
        ${
          isExpanded
            ? "progress-panel--expanded"
            : "progress-panel--collapsed"
        }
      `}
      aria-labelledby="chat-progress-title"
    >
      <button
        type="button"
        className="progress-summary"
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        aria-controls="chat-progress-content"
      >
        <div className="progress-summary__main">
          <OverallStatusIcon status={overallStatus} />

          <div className="progress-summary__text">
            <span
              id="chat-progress-title"
              className="progress-summary__title"
            >
              {summaryText}
            </span>

            {!isExpanded && error && (
              <span className="progress-summary__error">
                {error}
              </span>
            )}
          </div>
        </div>

        <span
          className={`
            progress-summary__chevron
            ${
              isExpanded
                ? "progress-summary__chevron--expanded"
                : ""
            }
          `}
          aria-hidden="true"
        >
          ⌄
        </span>
      </button>

      {isExpanded && (
        <div
          ref={contentRef}
          id="chat-progress-content"
          className="progress-panel__content"
        >
          {items.length === 0 && isProcessing && (
            <p className="progress-panel__waiting">
              در انتظار دریافت وضعیت پردازش...
            </p>
          )}

          <ol
            className="progress-list"
            aria-live="polite"
            aria-relevant="additions text"
          >
            {items.map((item) => (
              <ProgressItem key={item.id} event={item} />
            ))}
          </ol>

          {error && (
            <div
              className="progress-panel__error"
              role="alert"
            >
              {error}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
